import { Link, useParams } from "react-router-dom";
import { useSharedAccount } from "@/hooks/useData";
import { Logo, ThemeToggle } from "@/components/AppShell";
import OverviewDashboard from "@/components/analytics/OverviewDashboard";
import AdvancedStats from "@/components/analytics/AdvancedStats";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Eye, AlertCircle, Landmark, ChevronLeft } from "lucide-react";

export default function SharedAccountPage() {
  const { token } = useParams();
  const { data: shared, isLoading, error } = useSharedAccount(token);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-4 text-center" data-testid="shared-account-error">
        <AlertCircle className="h-8 w-8 text-[hsl(var(--warning))]" />
        <h1 className="font-display text-xl font-semibold">
          {error?.response?.status === 404 ? "This share link doesn't exist or was revoked." : "Couldn't load this account."}
        </h1>
        <p className="text-sm text-muted-foreground">Ask the owner for a fresh link.</p>
        <Button asChild variant="secondary"><Link to="/">Go to My Trading Room</Link></Button>
      </div>
    );
  }

  if (isLoading || !shared) {
    return (
      <div className="min-h-screen space-y-4 bg-background p-6">
        <Skeleton className="h-12 w-72" />
        <div className="grid grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40" />)}
        </div>
      </div>
    );
  }

  const acc = shared.account;

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="shared-account-page">
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur">
        <div className="flex h-14 items-center gap-3 px-4 lg:px-6">
          <Link to="/"><Logo /></Link>
          <div className="ml-2 hidden min-w-0 sm:block">
            <div className="truncate font-display text-sm font-semibold">{acc.name}</div>
            <div className="text-[11px] text-muted-foreground">shared by {shared.owner_name}</div>
          </div>
          <Badge variant="secondary" className="ml-2 gap-1 text-[10px]" data-testid="read-only-badge">
            <Eye className="h-3 w-3" /> Read-only
          </Badge>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle />
            <Button asChild size="sm" data-testid="shared-account-cta-button">
              <Link to="/login">Build your own</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 bg-surface-2 px-3 py-4 sm:px-4 lg:px-6">
        <div className="mx-auto w-full max-w-6xl space-y-4">
          <div className="rounded-2xl border bg-card p-4 sm:p-5" data-testid="shared-account-header">
            {shared.portfolio_token && (
              <Link to={`/shared/portfolio/${shared.portfolio_token}`} className="mb-3 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                data-testid="shared-back-to-portfolio">
                <ChevronLeft className="h-3.5 w-3.5" /> Back to portfolio
              </Link>
            )}
            <div className="flex items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                <Landmark className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0">
                <h1 className="font-display text-xl font-semibold tracking-tight sm:text-2xl">{acc.name}</h1>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px]">{(acc.platform || "MT").toUpperCase()}</Badge>
                  <span className="text-xs text-muted-foreground">{acc.broker || "\u2014"} · {acc.currency}</span>
                </div>
              </div>
            </div>
          </div>

          <OverviewDashboard accountId={acc.account_id || token} currency={acc.currency} symbols={acc.symbols || []} shareToken={token} />
          <AdvancedStats accountId={acc.account_id || token} currency={acc.currency} shareToken={token} />
        </div>
      </main>

      <footer className="border-t bg-card px-4 py-3 text-center text-xs text-muted-foreground">
        Powered by <span className="font-semibold text-foreground">My Trading Room</span>. Build your own trading dashboard in minutes.
      </footer>
    </div>
  );
}
